import { ConfigurationService, HistoryService, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpMethod } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { Endpoint } from './../constants/endpoints';
import { ApplicationRoute } from './../constants/routes';
import { setUserLocale } from './locale';
import { availableModulesMapper, ParametersModule } from './available-modules-mapper';
import { Establishment, Information, Profile, User } from '@philips-internal/oswald-ui-foundation';
import { HasUserTerms } from '../types/terms';

interface BootStrapEstablishment {
  cdEstabelecimento: string;
  dsRazaoSocial: string;
}

interface BootStrapProfile extends Profile {
  cdPerfilTasy: number;
  cdPessoaFisica: string;
  cdSetorAtendimento: number;
  dsLogin: string;
  dsPerfilTasy: string;
  dsSetorAtendimento: string;
  ieMedico: string;
  nmUsuario: string;
  nrSeqPerfilWsuite: string;
}

interface InformationBody {
  body: InformationMaper;
}

interface InformationMaper {
  currentEstablishment: BootStrapEstablishment;
  establishments: BootStrapEstablishment[];
  tasyParameters: ParametersModule[];
  twsParameters: ParametersModule[];
  userInformation: BootStrapProfile;
  userPhoto: string;
}

export interface HealthProfessionalUser extends User {
  isDoctor: boolean;
  physicalPersonId: string;
}

interface HealthProfessionalInformation extends Information {
  user: HealthProfessionalUser
}

function informationMapper(data: InformationBody): HealthProfessionalInformation {
  setUserLocale();

  let routeAfterLogin: HasUserTerms;

  HttpService.get(Endpoint.HAS_USER_TERMS).then(dataService => {
    routeAfterLogin = dataService.body as HasUserTerms;
    if (!routeAfterLogin.hasPrivacyNotice) {
      HistoryService.push(ApplicationRoute.PRIVACY_NOTICE);
    } else if (!routeAfterLogin.hasTermsOfUse) {
      HistoryService.push(ApplicationRoute.TERMOS_OF_USE);
    }
  });

  const { userInformation, userPhoto, currentEstablishment, establishments, twsParameters } = data.body;

  const changeOfEst = establishments && establishments.map((item: BootStrapEstablishment) => {
    return { id: item.cdEstabelecimento, tradingName: item.dsRazaoSocial };
  }) as Establishment[];

  return {
    establishment: {
      available: changeOfEst as Establishment[],
      current: {
        id: currentEstablishment.cdEstabelecimento,
        locale: 'undefined',
        name: currentEstablishment.dsRazaoSocial,
        tradingName: currentEstablishment.dsRazaoSocial,
      },
    },
    module: {
      available: availableModulesMapper(twsParameters),
    },
    profile: {
      available: [],
      current: {
        id: userInformation.cdPerfilTasy,
        name: userInformation.dsPerfilTasy,
      },
    },
    sector: {
      available: [],
      current: {
        id: userInformation.cdSetorAtendimento,
        name: userInformation.dsSetorAtendimento,
      },
    },
    user: {
      id: userInformation.nrSeqPerfilWsuite,
      image: userPhoto,
      isDoctor: userInformation.ieMedico === 'S',
      language: 'undefined',
      locale: 'undefined',
      name: userInformation.nmUsuario,
      physicalPersonId: userInformation.cdPessoaFisica,
      username: userInformation.dsLogin,
    },
  };
}

export function setupAuthentication(): void {
  ConfigurationService.setAuthenticationConfiguration({
    clientId: 'healthprofessional',
    encryptedToken: true,
    endpoints: {
      decryptToken: {
        method: HttpMethod.POST,
        url: Endpoint.DECRYPT_TOKEN,
      },
      information: {
        method: HttpMethod.GET,
        url: Endpoint.INFORMATION,
      },
      multiFactor: {
        validate: {
          method: HttpMethod.POST,
          url: Endpoint.AUTHENTICATION
        }
      },
      refresh: {
        method: HttpMethod.POST,
        url: Endpoint.AUTHENTICATION,
      },
      revoke: {
        method: HttpMethod.POST,
        url: Endpoint.LOGOUT,
      },
    },
    errorResponseMapper: {
      responseCodeMessageMap: {
        '412': 'tws.mfa.expired-or-invalid-code',
      },
      responseCodePath: 'code'
    },
    inactivity: {
      time: 15 * 60 * 1000,
    },
    routes: {
      custom: [ApplicationRoute.COOKIES_POLICY],
      forgotPassword: ApplicationRoute.FORGOT_PASSWORD,
      home: ApplicationRoute.SPOTLIGHT,
      inactivity: ApplicationRoute.INACTIVITY,
      login: ApplicationRoute.LOGIN,
    },
  });

  ConfigurationService.setEndpointMappers({
    // @ts-ignore
    information: informationMapper,
  });
}

